
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, LucideIcon } from 'lucide-react';

interface ServiceCardProps {
  icon: LucideIcon;
  flag: string;
  title: string;
  features: string[];
  color: 'green' | 'blue' | 'purple';
  onGetFreeAccess: () => void;
}

const ServiceCard = ({ icon: Icon, flag, title, features, color, onGetFreeAccess }: ServiceCardProps) => {
  const colorClasses = {
    green: {
      border: 'border-green-300 hover:border-green-500',
      bg: 'bg-gradient-to-br from-green-100 to-white',
      iconBg: 'bg-green-500',
      button: 'bg-green-600 hover:bg-green-700',
      accent: 'bg-green-500/20'
    },
    blue: {
      border: 'border-blue-300 hover:border-blue-500',
      bg: 'bg-gradient-to-br from-blue-100 to-white',
      iconBg: 'bg-blue-500',
      button: 'bg-blue-600 hover:bg-blue-700',
      accent: 'bg-blue-500/20'
    },
    purple: {
      border: 'border-purple-300 hover:border-purple-500',
      bg: 'bg-gradient-to-br from-purple-100 to-white',
      iconBg: 'bg-purple-500',
      button: 'bg-purple-600 hover:bg-purple-700',
      accent: 'bg-purple-500/20'
    }
  };

  const colors = colorClasses[color];

  return (
    <Card className={`group hover:shadow-2xl transition-all duration-500 hover:scale-105 border-4 ${colors.border} ${colors.bg} relative overflow-hidden h-full transform hover:-translate-y-2`}>
      <div className={`absolute top-0 right-0 w-24 h-24 ${colors.accent} rounded-full -translate-y-12 translate-x-12`}></div>
      <CardContent className="p-6 relative z-10 flex flex-col h-full">
        <div className="flex items-center mb-4">
          <div className={`${colors.iconBg} rounded-xl p-3 mr-3 shadow-xl group-hover:shadow-2xl transition-shadow duration-300`}>
            <Icon className="h-8 w-8 text-white" />
          </div>
          <span className="text-3xl animate-bounce" style={{ animationDuration: '3s' }}>{flag}</span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
        <div className="space-y-2 text-sm mb-4 flex-grow">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
              <span className="text-gray-700 font-medium">{feature}</span>
            </div>
          ))}
        </div>
        <Button 
          onClick={onGetFreeAccess}
          className={`w-full ${colors.button} text-white font-bold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
        >
          Get Free Access
        </Button>
      </CardContent>
    </Card>
  );
};

export default ServiceCard;
